import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TaskArrowIcon } from 'assets/icons/taskArrow.svg';
import ActivityHeader from 'components/ActivityHeader/ActivityHeader';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Textarea from 'components/Textarea/Textarea';
import { COMMENT_TASK } from 'constants/formFields';
import { DESCRIPTION_LENGTH, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { BUTTON_VARIANT, USERS_ROLE } from 'constants/interfaces';

import type IDataItem from 'store/slices/tasksManagement/interfaces/IDataItem';
import type { ITaskInfoComments } from './interfaces/ITaskInfoComments';

import styles from './sass/TaskInfoComments.module.scss';

function TaskInfoComments({
  comments,
  control,
  getUserInitials,
  onAddComment,
  userData,
  onDeleteComment,
  emptyListText,
}: ITaskInfoComments) {
  const { t } = useTranslation();
  const adminAccess = userData.role === USERS_ROLE.admin || userData.role === USERS_ROLE.companyAdmin;

  return (
    <div className={styles.commentsTab}>
      <div className={styles.addCommentSection}>
        <Controller
          control={control}
          name={COMMENT_TASK}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Textarea
              name={name}
              value={value}
              placeholder={t('common.typeYourCommentHere')}
              onChange={(e) => onChange(e.target.value.slice(0, DESCRIPTION_LENGTH))}
              error={error?.message}
              charactersLimit={DESCRIPTION_LENGTH}
              className={styles.commentTextarea}
            />
          )}
        />
        <Button
          title={t('common.addComment')}
          className={styles.addCommentButton}
          onClick={onAddComment}
        >
          {t('common.addComment')}
        </Button>
      </div>
      {comments && comments?.length > 0 ? comments?.map((comment) => (
        <div key={[comment.id, comment.createdAt].join('_')} className={styles.commentItem}>
          <div className={styles.commentIconWrapper}>
            <div className={styles.commentAvatar}>
              {getUserInitials(`${comment.user.firstName} ${comment.user.lastName}`)}
            </div>
            <ActivityHeader
              firstName={comment.user.firstName}
              lastName={comment.user.lastName}
              createdAt={comment.createdAt}
            />
          </div>
          <div className={styles.commentContent}>
            { (comment.data as IDataItem)?.before?.status ? (
              <div className={styles.contentStatus}>
                <div className={`${styles.statusBadge} 
                    ${styles[`${(comment.data as IDataItem)?.before?.status}`]}`}
                >
                  {(comment.data as IDataItem)?.before?.status}
                </div>
                <TaskArrowIcon />
                <div className={`${styles.statusBadge} 
                    ${styles[`${(comment.data as IDataItem)?.after?.status}`]}`}
                >
                  {(comment.data as IDataItem)?.after?.status}
                </div>

              </div>
            ) : null}
            {!(comment.data as IDataItem)?.before?.status && comment.comment ? (
              <div className={styles.commentText}>
                {comment.comment}
              </div>
            ) : null}
            {(adminAccess
            || (userData.id === comment.user.id
              && userData.permissionTaskManagement !== TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY))
            && !(comment.data as IDataItem)?.before?.status ? (
              <div className={styles.commentActions}>
                <Button
                  title={t('common.delete')}
                  variant={BUTTON_VARIANT.ghost}
                  onClick={() => onDeleteComment(comment.id)}
                  className={styles.deleteButton}
                >
                  {t('common.delete')}
                </Button>
              </div>
              ) : null}
          </div>
          { (comment.data as IDataItem)?.before?.status && comment.comment ? (
            <div className={styles.contentComment}>
              <span className={styles.titleBox}>
                {t('taskManagement.commentToChanging')}
              </span>
              <span>
                {comment.comment}
              </span>
            </div>
          ) : null}
        </div>
      )) : <EmptyList text={emptyListText} />}
    </div>
  );
}

export default TaskInfoComments;
